import { Tooltip } from '@chakra-ui/react'
import React from 'react'
import styled from 'styled-components'
import { FillSliderFillProps } from '~/components/FillSlider'
import { tryExtractPixelProperty } from '~/utils/css'

interface MultiFillSliderWrapperProps {
  height?: number
  width?: number | string
  borderRadius?: number | string
  backgroundColor?: string
  border?: string
  borderStyle?: string
  borderColor?: string
  margin?: string
  outline?: string
  outlineColor?: string
}

const MultiFillSliderWrapper = styled.div<MultiFillSliderWrapperProps>`
  display: flex;
  height: ${(props) => props.height ?? 24}px;
  width: ${(props) =>
    !isNaN(props.width as number) ? `${props.width ?? 100}px` : props.width};
  background-color: ${(props) => props.backgroundColor ?? 'white'};
  margin: ${(props) => tryExtractPixelProperty(props.margin)};
  border: ${(props) => props.border ?? ''};
  border-color: ${(props) => props.borderColor ?? ''};
  border-radius: ${(props) =>
    tryExtractPixelProperty(props.borderRadius) ?? '8px'};
  border-style: ${(props) => props.borderStyle ?? ''};
  outline: ${(props) => props.outline ?? ''};
  outline-color: ${(props) => props.outlineColor ?? ''};
  justify-content: left;
  align-items: center;
  position: relative;
`

export interface FillValues {
  startPercentage: number
  endPercentage: number
  tooltipText?: string
  fillProps?: MultiFillSliderFillProps
}

export interface MultiFillSliderProps extends MultiFillSliderWrapperProps {
  fillValues: FillValues[]
  animate?: boolean
  animationDelay?: number
  animationTime?: number
  children?: React.ReactNode
}

export interface MultiFillSliderFillProps extends FillSliderFillProps {
  left?: number
  fillColor: string
  fillBorderRadius?: number | string
  fillBorder?: string
  fillBorderColor?: string
  fillOutline?: string
  fillOutlineColor?: string
}

const FillSliderFill = styled.div<MultiFillSliderFillProps>`
  height: ${(props) => props.height ?? 24}px;
  left: ${(props) => props.left ?? 0}%;
  width: ${(props) => props.width ?? 100}%;
  border: ${(props) => props.border ?? ''};
  border-color: ${(props) => props.borderColor ?? ''};
  border-radius: ${(props) =>
    tryExtractPixelProperty(props.borderRadius) ?? '8px'};
  background-color: ${(props) => props.fillColor};
  outline: ${(props) => props.outline ?? ''};
  outline-color: ${(props) => props.outlineColor ?? ''};
  transition: ${(props) =>
    props.animate
      ? `width ${props.animationTime?.toString() ?? 1}s ease-in-out`
      : ''};
  transition-delay: ${(props) => props.animationDelay ?? 0.1}s;
  position: absolute;
`

const MultiFillSlider = ({
  fillValues,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  outline,
  outlineColor,
  height,
  width,
  margin,
  animate,
  animationDelay,
  animationTime,
  children,
}: MultiFillSliderProps) => {
  const getFills = () => {
    const fillJSX: JSX.Element[] = []
    for (let i = 0; i < fillValues.length; i++) {
      const fillValue = fillValues[i]
      const { startPercentage, endPercentage, fillProps } = fillValue
      const fillWidth = endPercentage - startPercentage
      const fillLeft = startPercentage
      fillJSX.push(
        <Tooltip label={fillValue.tooltipText} key={'tooltip-' + i}>
          <FillSliderFill
            key={i}
            fillColor={fillProps?.fillColor ?? 'red'}
            height={height}
            borderRadius={fillProps?.fillBorderRadius ?? borderRadius}
            border={fillProps?.fillBorder ?? ''}
            borderColor={fillProps?.fillBorderColor}
            left={fillLeft * 100}
            width={fillWidth * 100}
            animate={animate}
            animationDelay={animationDelay}
            animationTime={animationTime}
            outline={fillProps?.fillOutline}
            outlineColor={fillProps?.fillOutlineColor}
          />
        </Tooltip>,
      )
    }
    return fillJSX
  }
  return (
    <MultiFillSliderWrapper
      height={height}
      width={width}
      margin={margin}
      borderRadius={borderRadius}
      border={border}
      borderColor={borderColor}
      borderStyle={borderStyle}
      backgroundColor={backgroundColor}
      outline={outline}
      outlineColor={outlineColor}
    >
      {getFills()}
      {children ?? ''}
    </MultiFillSliderWrapper>
  )
}

export default MultiFillSlider
